import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { useMemo, useRef, useState } from 'react';
import {
  type CreditV1ClaimNexusRecordRequest,
  type CreditV1GetMyNexusHistroyReply,
  claimNexusRecord,
  getMyNexusHistory,
} from '@/app/apis';
import { toast } from '@/app/components/ui/use-toast';
import { isEnterpriseDistribution } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { FormTabType, FreeCreditsTask, FreeCreditsTaskGroups, ModelVersion } from '@/app/types';
import { type FreeCreditsTaskName, NewbieTaskName, NormalFreeCreditsTaskName } from '../constants';
import { getRemainingTaskReward } from './free-credits-task-reward';
import { useCredits } from './use-credits';
import { useFetch } from './use-fetch';

// 新手任务的名称前缀或标识
export const NEWBIE_TASK_PREFIX = 'newbie_task_';

type TaskPriorityKey = Exclude<FreeCreditsTaskName, 'mission_watch_ad_credit'>;
type ClaimRuleSet = NonNullable<CreditV1ClaimNexusRecordRequest['rule_set']>;

// 任务排序配置
const taskPriority: Record<TaskPriorityKey, number> = {
  [NormalFreeCreditsTaskName.invitecodecatalyst_credit]: 10,
  [NewbieTaskName.newbie_task_i2v_v2_credit]: 20,
  [NewbieTaskName.newbie_task_i2v_Q1_credit]: 30,
  [NewbieTaskName.newbie_task_c2v_Q1_credit]: 40,
  [NewbieTaskName.newbie_task_template_credit]: 50,
  [NewbieTaskName.newbie_task_i2v_Q2_credit]: 20,
  [NewbieTaskName.newbie_task_c2v_Q2_credit]: 30,
  [NewbieTaskName.newbie_task_material_create_credit]: 40,
  [NewbieTaskName.newbie_task_use_material_credit]: 50,
  [NewbieTaskName.newbie_task_image_generate_credit]: 42,
  [NewbieTaskName.newbie_task_image_to_video_credit]: 52,
  [NormalFreeCreditsTaskName.download_app_credit]: 60,
  [NormalFreeCreditsTaskName.questionnaire_credit]: 70,
  [NormalFreeCreditsTaskName.every_day_credit]: 80,
  [NormalFreeCreditsTaskName.new_user_credit]: 90,
  [NormalFreeCreditsTaskName.subscribe_email_credit]: 100,
  [NormalFreeCreditsTaskName.renewal_questionnaire_credit]: 65,
};

const EMPTY_TASKS: FreeCreditsTask[] = [];

const toFreeCreditsTasks = (tasks: CreditV1GetMyNexusHistroyReply['history']): FreeCreditsTask[] =>
  (tasks ?? []).filter((task) => Boolean(task.name)) as FreeCreditsTask[];

/**
 * 新版分组状态优先于旧 history 的同名任务。
 * 旧 history 对 Q2 待领取任务可能仍返回 finished=true，浮标必须读取分组里的 pending_claim。
 */
export const mergeTaskCenterNewbieTasks = (
  historyTasks: FreeCreditsTask[],
  groupedNewbieTasks: FreeCreditsTask[],
): FreeCreditsTask[] => {
  const groupedByName = new Map(groupedNewbieTasks.map((task) => [task.name, task]));
  const merged = historyTasks.map((task) => {
    const groupedTask = groupedByName.get(task.name);
    return groupedTask ? { ...task, ...groupedTask } : task;
  });
  const historyNames = new Set(historyTasks.map((task) => task.name));

  groupedNewbieTasks.forEach((task) => {
    if (!historyNames.has(task.name)) {
      merged.push(task);
    }
  });

  return merged;
};

/**
 * 新版接口只要出现任一分组字段，就以三组字段为准；三个字段都缺失时才回退旧 history。
 * 这样可以区分“新版接口返回空分组”和“旧版接口尚未提供分组字段”。
 */
export const getFreeCreditsTaskGroups = (data: CreditV1GetMyNexusHistroyReply): FreeCreditsTaskGroups | null => {
  const hasGroupedTaskResponse =
    data.newbie_tasks !== undefined ||
    data.advanced_creation_tasks !== undefined ||
    data.advertisement_tasks !== undefined;

  if (!hasGroupedTaskResponse) {
    return null;
  }

  return {
    newbieTasks: toFreeCreditsTasks(data.newbie_tasks),
    advancedCreationTasks: toFreeCreditsTasks(data.advanced_creation_tasks),
    advertisementTasks: toFreeCreditsTasks(data.advertisement_tasks),
  };
};

// 任务排序函数
const sortTasks = (tasks: FreeCreditsTask[]): FreeCreditsTask[] => {
  // 使用 [...tasks] 创建一个新数组进行排序，避免直接修改原数组
  return [...tasks].sort((a, b) => {
    // 将任务名称断言为 TaskKey，以便在 taskPriority 中查找
    const aPriority = taskPriority[a.name as TaskPriorityKey] || 999;
    const bPriority = taskPriority[b.name as TaskPriorityKey] || 999;
    return aPriority - bPriority;
  });
};

// 将原始任务数据转换为增强的任务数据
const convertToEnhancedTask = (task: FreeCreditsTask): EnhancedNewbieTask => {
  const totalProgress = task.total_times ?? 1;

  return {
    ...task,
    totalReward: getRemainingTaskReward(task),
    eachReward: task.credits_award ?? 0,
    totalProgress,
    currentProgress: task.finished ? totalProgress : (task.finished_times ?? 0),
  };
};

// 增强的新手任务接口，包含配置信息
export interface EnhancedNewbieTask extends FreeCreditsTask {
  totalReward: number;
  eachReward: number;
  totalProgress: number;
  currentProgress: number;
  modelVersion?: ModelVersion;
  taskType?: FormTabType;
}

/**
 * 免费积分任务管理 Hook
 *
 * 功能：
 * 1. 获取和刷新任务列表
 * 2. 查询某个提交是否属于新手任务（用于埋点）
 * 3. 提供任务查询和状态管理功能
 * 4. 提供增强的任务数据（包含配置信息）
 */
export const useFreeCreditsTasks = () => {
  const t = useTranslations();
  const { freeCreditsTaskGroups, freeCreditsTasks, updateFreeCreditsTaskGroups, updateFreeCreditsTasks, userInfo } =
    useStore((state) => ({
      freeCreditsTaskGroups: state.freeCreditsTaskGroups,
      freeCreditsTasks: state.freeCreditsTasks,
      updateFreeCreditsTaskGroups: state.updateFreeCreditsTaskGroups,
      updateFreeCreditsTasks: state.updateFreeCreditsTasks,
      userInfo: state.userInfo,
    }));
  const taskGroups = freeCreditsTaskGroups;
  const [claimingTaskName, setClaimingTaskName] = useState<ClaimRuleSet | null>(null);
  const claimingTaskNameRef = useRef<ClaimRuleSet | null>(null);

  const { updateCreditsAsync } = useCredits();

  // 使用 useFetch 管理 API 请求
  const {
    run: fetchFreeCreditTasksRaw,
    runAsync: fetchFreeCreditTasksRawAsync,
    loading,
    error,
  } = useFetch(getMyNexusHistory, {
    manual: true,
    onSuccess: (response) => {
      if (!response?.data) {
        return;
      }
      const historyTasks = toFreeCreditsTasks(response.data.history);
      const groupedTasks = getFreeCreditsTaskGroups(response.data);
      updateFreeCreditsTaskGroups(groupedTasks);
      const sortedTasks = sortTasks(
        groupedTasks ? mergeTaskCenterNewbieTasks(historyTasks, groupedTasks.newbieTasks) : historyTasks,
      );
      updateFreeCreditsTasks(sortedTasks);
      updateCreditsAsync();
      return sortedTasks;
    },
  });

  const { runAsync: claimNexusRecordAsync } = useFetch(claimNexusRecord, { manual: true });

  const fetchFreeCreditTasks = useMemoizedFn(() => {
    if (isEnterpriseDistribution) {
      updateFreeCreditsTaskGroups(null);
      updateFreeCreditsTasks([]);
      updateCreditsAsync();
      return;
    }

    fetchFreeCreditTasksRaw();
  });

  const fetchFreeCreditTasksAsync = useMemoizedFn(async (): Promise<FreeCreditsTaskGroups | null> => {
    if (isEnterpriseDistribution) {
      updateFreeCreditsTaskGroups(null);
      updateFreeCreditsTasks([]);
      updateCreditsAsync();
      return null;
    }

    const response = await fetchFreeCreditTasksRawAsync();
    return response?.data ? getFreeCreditsTaskGroups(response.data) : null;
  });

  const claimTaskReward = useMemoizedFn(async (ruleSet: ClaimRuleSet): Promise<boolean> => {
    if (!userInfo?.id || claimingTaskNameRef.current) {
      return false;
    }

    claimingTaskNameRef.current = ruleSet;
    setClaimingTaskName(ruleSet);
    try {
      const response = await claimNexusRecordAsync({
        body: {
          user_id: userInfo.id,
          rule_set: ruleSet,
        },
      });
      if (!response.data?.success) {
        toast({ type: 'error', message: t('popup.toast.fetchError') });
        return false;
      }

      toast({
        type: 'success',
        message: t('newbieTask.completeToast', { points: response.data.credits_award ?? 0 }),
      });
      await fetchFreeCreditTasksRawAsync();
      return true;
    } catch {
      // useFetch 已统一展示网络及业务错误，此处只负责收敛异步错误并恢复按钮状态。
      return false;
    } finally {
      claimingTaskNameRef.current = null;
      setClaimingTaskName(null);
    }
  });

  // 获取所有新手任务（包括已完成和未完成的）- 增强版本
  const [allNewbieTasks, unfinishedNewbieTasks] = useMemo(() => {
    const rawNewbieTasks =
      taskGroups !== null
        ? taskGroups.newbieTasks
        : freeCreditsTasks.filter(
            (task) =>
              task.name?.startsWith(NEWBIE_TASK_PREFIX) || task.name === NormalFreeCreditsTaskName.new_user_credit,
          );

    // 转换为增强的任务数据
    const enhancedTasks = rawNewbieTasks.map(convertToEnhancedTask);

    return [enhancedTasks, enhancedTasks.filter((task) => task.totalReward > 0)];
  }, [freeCreditsTasks, taskGroups]);

  /**
   * 根据新手任务名称获取预计的新手任务奖励
   * @param newbieTaskName - 新手任务名称
   * @returns 预计的新手任务奖励
   */
  const getExpectedNewbieReward = useMemoizedFn((newbieTaskName: NewbieTaskName) => {
    return unfinishedNewbieTasks.find((task) => task.name === newbieTaskName)?.eachReward ?? 0;
  });

  return {
    advertisementTasks: taskGroups?.advertisementTasks ?? EMPTY_TASKS,
    advancedCreationTasks: taskGroups?.advancedCreationTasks ?? EMPTY_TASKS,
    claimTaskReward,
    claimingTaskName,
    allNewbieTasks,
    error,
    fetchFreeCreditTasks,
    fetchFreeCreditTasksAsync,
    freeCreditsTasks,
    hasGroupedTaskResponse: taskGroups !== null,
    loading,
    newbieTasks: taskGroups?.newbieTasks ?? EMPTY_TASKS,
    unfinishedNewbieTasks,
    getExpectedNewbieReward,
  };
};
